"use client";

import React from "react";
import { PenLine, Mic, Shield, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contributionOptions = [{
  title: "Written Reflections",
  description: "Take your time to craft your story in writing. Share your essays, poems, or journal entries in a safe, supportive space at your own pace.",
  icon: PenLine
}, {
  title: "Guided Conversations",
  description: "Sit down with one of our compassionate listeners for a gentle, recorded interview where you can speak freely and safely from the heart.",
  icon: Mic
}, {
  title: "Anonymous Sharing",
  description: "Share your experiences without revealing your identity. Your story can inspire and heal others while you maintain complete privacy.",
  icon: Shield
}];
export default function Article() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="article" className="py-24 md:py-32 bg-secondary/30 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-background/50 blur-[100px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 max-w-6xl relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 tracking-tight">
            Your Story, <span className="text-ring relative whitespace-nowrap">
              Your Way
              <svg className="absolute -bottom-2 left-0 w-full h-3 text-ring/30" viewBox="0 0 100 10" preserveAspectRatio="none">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="2" fill="transparent" />
              </svg>
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            We believe every voice deserves to be heard in a way that feels safe and comfortable. 
            Choose the path that best honors your journey and allows you to share your experiences 
            with courage and creativity.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {contributionOptions.map((option, i) => {
          const Icon = option.icon;
          return <div key={option.title} data-index={i} className={`transition-all duration-1000 ease-out delay-[${i * 150}ms] ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Card className="h-full border-none shadow-xl shadow-primary/5 bg-background/80 backdrop-blur-sm rounded-[2.5rem] hover:-translate-y-2 transition-transform duration-300">
                  <CardHeader className="pt-10 px-8 pb-4">
                    <div className="w-16 h-16 rounded-2xl bg-ring/10 flex items-center justify-center mb-6 text-ring">
                      <Icon className="w-8 h-8" strokeWidth={1.5} />
                    </div>
                    <CardTitle className="font-serif text-2xl text-foreground">
                      {option.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-8 pb-10">
                    <CardDescription className="text-base text-muted-foreground leading-relaxed">
                      {option.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-16 md:mt-24 text-center">
          <Button asChild size="lg" className="rounded-full px-8 py-6 text-lg shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all">
            <Link to="/ShareYourStoryPage">
              Begin Your Journey
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}